import type { OrderDetails } from '@/entities/order/model'
import type {
  PoaIdFailureReason,
  PoaIdType,
  PoaOrderFlowRecord,
  PoaSubmission,
  RestrictedCategory,
  RsaRefusalReason,
  RsaRefusalRecord,
} from '@/features/order/model'
import { IconCheck, IconId, IconX } from '@tabler/icons-react'
import { useId, useState } from 'react'
import { getRestrictedCategories } from '@/features/order/lib/order.poa'
import { usePoaFlowStore } from '@/features/order/model'
import classes from './PoaFlowPanel.module.css'

interface PoaFlowPanelProps {
  order: OrderDetails
  onRsaRefusalRecorded?: (record: RsaRefusalRecord) => void
  /**
   * Called once every applicable check has an outcome and the team member confirms
   * the handover. Team member initials are captured afterwards by the existing Team
   * Member Initials feature, on its own toggle — the checks do not collect them.
   *
   * Resolve to `'awaiting-verification'` when completion continues in the collection
   * code step (BD-8761): the answers are then kept until that step completes the order.
   * Resolve to `'not-submitted'` when nothing was sent, such as when the feature was
   * turned off mid-flow, so the answers are not discarded as if they were recorded.
   */
  onComplete?: (submission: PoaSubmission) => Promise<PoaCompleteOutcome | void> | PoaCompleteOutcome | void
}

/** How the handover continues once the checks are confirmed. */
export type PoaCompleteOutcome = 'completed' | 'awaiting-verification' | 'not-submitted'

type RsaOutcome = 'passed' | 'refused'
type PoaStep = 'rsa' | 'age' | 'id' | 'refusal-reason' | 'completion'

const REFUSAL_REASON_LABELS: Record<RsaRefusalReason, string> = {
  'suspected-intoxication': 'Suspected intoxication',
  'suspected-secondary-supply': 'Suspected secondary supply',
  'other': 'Other',
}

const REFUSAL_REASON_DESCRIPTIONS: Partial<Record<RsaRefusalReason, string>> = {
  'suspected-intoxication': 'Person appears affected by alcohol or drugs.',
  'suspected-secondary-supply': 'Risk of secondary supply to minor or intox',
}

const ID_TYPE_LABELS: Record<PoaIdType, string> = {
  'drivers-licence': 'Driver’s licence',
  'learners-permit': 'Learner’s permit',
  'passport': 'Passport',
  'proof-of-age-card': 'Proof of Age Card',
}

const ID_FAILURE_LABELS: Record<PoaIdFailureReason, string> = {
  'under-18': 'Minor / Under 18',
  'no-valid-id': 'No ID / Invalid ID',
}

function formatMinimumDob(now = new Date()): string {
  const minimumDob = new Date(now)
  minimumDob.setFullYear(minimumDob.getFullYear() - 18)
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(minimumDob)
}

/**
 * The purple (or red, on the refusal path) small-caps label with its leading dot
 * that opens every card in the design.
 */
function SectionLabel({ children, tone = 'accent' }: { children: string, tone?: 'accent' | 'negative' }) {
  return (
    <span className={classes.sectionLabel} data-tone={tone}>
      <span className={classes.sectionDot} aria-hidden />
      {children}
    </span>
  )
}

function StepHeading({ label, question, guidance }: {
  label: string
  question: string
  guidance: string
}) {
  return (
    <>
      <SectionLabel>{label}</SectionLabel>
      <h2 className={classes.question}>{question}</h2>
      <p className={classes.guidance}>{guidance}</p>
    </>
  )
}

/**
 * The red-bordered script the team member reads out when something has to come off
 * the order. It nests inside whichever card is terminal for the path taken — the
 * failed-ID card on the ID path, the completion card otherwise.
 */
function RefusalScript({ instruction, example }: { instruction: string, example: string }) {
  return (
    <div className={classes.refusalScript} data-testid="poa-refusal-script">
      <strong>{instruction}</strong>
      <em>{example}</em>
    </div>
  )
}

interface CheckSummaryRowProps {
  label: string
  value: string
  detail?: string
  tone?: 'positive' | 'negative'
  testId: string
  changeLabel: string
  onChange: () => void
}

function CheckSummaryRow({ label, value, detail, tone = 'positive', testId, changeLabel, onChange }: CheckSummaryRowProps) {
  const StatusIcon = tone === 'positive' ? IconCheck : IconX
  return (
    <div className={`${classes.card} ${classes.summaryCard}`} data-tone={tone} data-testid={testId}>
      <div className={classes.summary}>
        <span className={classes.summaryIcon} data-tone={tone}>
          <StatusIcon aria-hidden size={18} stroke={2.4} />
        </span>
        <div className={classes.summaryContent}>
          <p className={classes.summaryLabel}>{label}</p>
          <p className={classes.summaryValue}>{value}</p>
          {detail && <p className={classes.summaryDetail}>{detail}</p>}
        </div>
        <button type="button" aria-label={changeLabel} className={classes.changeButton} onClick={onChange}>
          Change
        </button>
      </div>
    </div>
  )
}

interface BinaryChoiceProps {
  selected: boolean
  tone: 'positive' | 'negative'
  title: string
  description: string
  icon: React.ReactNode
  onClick: () => void
}

function BinaryChoice({ selected, tone, title, description, icon, onClick }: BinaryChoiceProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={classes.choice}
      data-selected={selected || undefined}
      data-tone={tone}
      onClick={onClick}
    >
      <span className={classes.choiceIcon} data-tone={tone}>{icon}</span>
      <span className={classes.choiceTitle}>{title}</span>
      <span className={classes.choiceDescription}>{description}</span>
    </button>
  )
}

function RsaAssessment({ selectedOutcome, onPass, onRefuse }: {
  selectedOutcome: RsaOutcome | null
  onPass: () => void
  onRefuse: () => void
}) {
  return (
    <div className={`${classes.card} ${classes.stepCard}`} data-testid="rsa-assessment-step">
      <StepHeading
        label="STEP 1 OF 2"
        question="Is the person fit to receive liquor?"
        guidance="RSA check. Watch for signs of intoxication or secondary supply"
      />
      <div className={classes.choiceGrid}>
        <BinaryChoice
          selected={selectedOutcome === 'passed'}
          tone="positive"
          title="Yes, fit"
          description="No RSA risks"
          icon={<IconCheck aria-hidden size={24} stroke={2.4} />}
          onClick={onPass}
        />
        <BinaryChoice
          selected={selectedOutcome === 'refused'}
          tone="negative"
          title="Refuse"
          description="Remove liquor products"
          icon={<IconX aria-hidden size={24} stroke={2.4} />}
          onClick={onRefuse}
        />
      </div>
    </div>
  )
}

function AgeCheckStep({ stepNumber, totalSteps, selectedOutcome, onLooks25OrOlder, onCheckId }: {
  stepNumber: number
  totalSteps: number
  selectedOutcome: 'looks-25-or-older' | 'id-required' | null
  onLooks25OrOlder: () => void
  onCheckId: () => void
}) {
  return (
    <div className={`${classes.card} ${classes.stepCard}`} data-testid="poa-age-check-step">
      <StepHeading
        label={`STEP ${stepNumber} OF ${totalSteps}`}
        question="Does the person look 25 or older?"
        guidance="If you’re unsure, choose “Check ID”"
      />
      <div className={classes.choiceGrid}>
        <BinaryChoice
          selected={selectedOutcome === 'looks-25-or-older'}
          tone="positive"
          title="Yes, 25+"
          description="No ID needed"
          icon={<IconCheck aria-hidden size={24} stroke={2.4} />}
          onClick={onLooks25OrOlder}
        />
        <BinaryChoice
          selected={selectedOutcome === 'id-required'}
          tone="negative"
          title="Check ID"
          description="Under or unsure"
          icon={<IconId aria-hidden size={24} stroke={2} />}
          onClick={onCheckId}
        />
      </div>
    </div>
  )
}

function RefusalReasonStep({ reason, otherReason, onReasonChange, onOtherReasonChange }: {
  reason: RsaRefusalReason | null
  otherReason: string
  onReasonChange: (reason: RsaRefusalReason) => void
  onOtherReasonChange: (value: string) => void
}) {
  return (
    <div className={`${classes.card} ${classes.reasonCard}`} data-testid="poa-refusal-reason-step">
      <div className={classes.reasonHeading}>
        <SectionLabel tone="negative">REASON FOR REFUSAL</SectionLabel>
        <h2 className={classes.question}>What’s the concern?</h2>
      </div>
      <p className={classes.guidance}>Liquor items will be returned.</p>
      <fieldset className={classes.reasonGroup}>
        <legend className={classes.visuallyHidden}>RSA refusal reason</legend>
        {(Object.entries(REFUSAL_REASON_LABELS) as [RsaRefusalReason, string][]).map(([value, label]) => (
          <label key={value} className={classes.reasonChoice} data-selected={reason === value || undefined}>
            <input
              type="radio"
              aria-label={label}
              name="rsa-refusal-reason"
              value={value}
              checked={reason === value}
              onChange={() => onReasonChange(value)}
            />
            <span>
              <strong>{label}</strong>
              {REFUSAL_REASON_DESCRIPTIONS[value] && <small>{REFUSAL_REASON_DESCRIPTIONS[value]}</small>}
            </span>
          </label>
        ))}
      </fieldset>
      {reason === 'other' && (
        <div className={classes.otherReasonField}>
          <textarea
            aria-label="Reason for refusal"
            aria-required="true"
            autoFocus
            maxLength={250}
            placeholder="Please tell us a more about what’s the reason for refusal"
            rows={3}
            value={otherReason}
            onChange={event => onOtherReasonChange(event.currentTarget.value)}
          />
          <small>* Required</small>
        </div>
      )}
    </div>
  )
}

function IdVerificationStep({ idType, failureReason, minimumDob, script, onIdType, onFailure }: {
  idType?: PoaIdType
  failureReason?: PoaIdFailureReason
  minimumDob: string
  script?: { instruction: string, example: string }
  onIdType: (idType: PoaIdType) => void
  onFailure: (reason?: PoaIdFailureReason) => void
}) {
  // The ID screen is the last screen on this path: the design puts no separate
  // "all checks complete" card after it, so the footer action confirms from here.
  //
  // The two answers are independent, not either/or: the usual failing path is to
  // check an ID, find it wanting, and record why — a learner's permit showing the
  // customer is under 18 is both a sighted ID and a failure.
  //
  // The failure reasons toggle: tapping the selected one clears it. A mis-tap here
  // otherwise traps the team member into reporting a refusal that did not happen,
  // since the pair has no "neither" option to switch back to.
  // `fieldset`/`legend` is avoided here: the legend's special rendering does not take
  // part in the flex rhythm, and these are toggle buttons rather than form controls.
  const idTypeLabelId = useId()
  const failureLabelId = useId()

  return (
    <div className={classes.idStep} data-testid="poa-id-check-step">
      <div className={classes.card}>
        <div className={classes.idHeader}>
          <SectionLabel>VERIFY ID</SectionLabel>
          <div className={classes.idPrompt}>
            <div className={classes.dobBlock}>
              <p>Date of birth must be on or before</p>
              <strong>{minimumDob}</strong>
            </div>
            <p className={classes.optionLegend} id={idTypeLabelId}>Which ID did you check?</p>
          </div>
        </div>
        <div className={classes.optionGrid} role="group" aria-labelledby={idTypeLabelId}>
          {(Object.entries(ID_TYPE_LABELS) as [PoaIdType, string][]).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={idType === value}
              className={classes.optionButton}
              data-selected={idType === value || undefined}
              onClick={() => onIdType(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.card} data-tone="negative" data-testid="poa-id-failure-card">
        <div className={classes.failureSection}>
          <div className={classes.failureHeading}>
            <p className={classes.failureLegend} id={failureLabelId}>If customer failed ID check?</p>
            <p className={classes.optionHint}>Select reason</p>
          </div>
          <div className={classes.optionGrid} role="group" aria-labelledby={failureLabelId}>
            {(Object.entries(ID_FAILURE_LABELS) as [PoaIdFailureReason, string][]).map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={failureReason === value}
                className={classes.optionButton}
                data-selected={failureReason === value || undefined}
                data-tone="negative"
                onClick={() => onFailure(failureReason === value ? undefined : value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* Belongs to the selected reason, so it appears and disappears with it.
            Nothing stands alone: the removal is carried by the footer action. */}
        {failureReason && script && <RefusalScript {...script} />}
      </div>
    </div>
  )
}

function CompletionStep({ removalLabel, script }: {
  removalLabel?: string
  script?: { instruction: string, example: string }
}) {
  return (
    <div className={classes.card} data-testid="poa-completion-step">
      <div className={classes.completionMessage}>
        {/* Green even on a refusal: the checks are complete, and the red script
            below is what communicates that something comes off the order. */}
        <span className={classes.completionIcon}>
          <IconCheck aria-hidden size={24} stroke={2.4} />
        </span>
        <h2>All checks complete</h2>
        {removalLabel
          ? (
              <p>
                <span>{`Make sure to remove the ${removalLabel} from the order`}</span>
                <span>Confirm to release the order</span>
              </p>
            )
          : <p>Confirm to release the order.</p>}
      </div>
      {script && <RefusalScript {...script} />}
    </div>
  )
}

function getInitialStep(
  requiresRsa: boolean,
  stillRequiresAgeAfterRefusal: boolean,
  record?: PoaOrderFlowRecord,
): PoaStep {
  if (requiresRsa && !record?.rsaOutcome)
    return 'rsa'
  if (record?.rsaOutcome === 'refused' && !record.rsaRefusal)
    return 'refusal-reason'
  const needsAge = record?.rsaOutcome !== 'refused' || stillRequiresAgeAfterRefusal
  if (needsAge && !record?.ageOutcome)
    return 'age'
  // The ID screen is terminal, so a flow reopened part-way through it lands back
  // there rather than skipping to a completion screen the design does not have.
  if (record?.ageOutcome === 'id-required')
    return 'id'
  return 'completion'
}

/** Single-category name, or undefined when more than one category is in play. */
function soleCategory(categories: RestrictedCategory[]): RestrictedCategory | undefined {
  return categories.length === 1 ? categories[0] : undefined
}

/** Copy for the removal CTA — "liquor items", "tobacco items" or "restricted items". */
function removalCtaLabel(removed: RestrictedCategory[]): string {
  const sole = soleCategory(removed)
  return sole ? `${sole} items` : 'restricted items'
}

/**
 * The read-aloud script, which is worded differently depending on why the items are
 * coming off the order. Matches the approved wording for each scenario.
 */
function refusalScriptCopy(removed: RestrictedCategory[], source: 'rsa' | 'id') {
  const sole = soleCategory(removed)
  const spoken = removed.length > 0 ? removed.join('/') : 'restricted items'
  if (source === 'rsa') {
    return {
      instruction: `Politely refuse ${sole ?? 'restricted item'} supply and remove from order`,
      example: `e.g. “I’m sorry, I can’t supply you ${spoken} today.”`,
    }
  }
  return {
    instruction: sole
      ? `Politely refuse ${sole} supply and remove restricted items from order.`
      : 'Politely refuse supply and remove restricted items from order.',
    example: `“I’m sorry, I can’t supply you ${spoken} today.”`,
  }
}

/** Guided POA flow. Draft answers persist locally by order until final submission. */
export function PoaFlowPanel({ order, onRsaRefusalRecorded, onComplete }: PoaFlowPanelProps) {
  const detectedCategories = getRestrictedCategories(order)
  const categories: RestrictedCategory[] = detectedCategories.length > 0
    ? detectedCategories
    : ['liquor', 'tobacco']
  const requiresRsa = categories.includes('liquor')
  const totalSteps = requiresRsa ? 2 : 1
  const stillRequiresAgeAfterRefusal = categories.includes('tobacco')
  const storedRecord = usePoaFlowStore(state => state.records[order.orderId])
  const setStoredRsaOutcome = usePoaFlowStore(state => state.setRsaOutcome)
  const setStoredRefusalDraft = usePoaFlowStore(state => state.setRsaRefusalDraft)
  const storeRsaRefusal = usePoaFlowStore(state => state.recordRsaRefusal)
  const setStoredAgeOutcome = usePoaFlowStore(state => state.setAgeOutcome)
  const setStoredIdType = usePoaFlowStore(state => state.setIdType)
  const setStoredIdFailure = usePoaFlowStore(state => state.setIdFailureReason)
  const clearOrderRecord = usePoaFlowStore(state => state.clearOrder)
  const rsaOutcome = storedRecord?.rsaOutcome ?? null
  const refusalReason = storedRecord?.rsaRefusalDraft?.reason ?? null
  const otherRefusalReason = storedRecord?.rsaRefusalDraft?.otherReason ?? ''
  const recordedRefusal = storedRecord?.rsaRefusal ?? null
  const ageOutcome = storedRecord?.ageOutcome ?? null
  const idType = storedRecord?.idType
  const idFailureReason = storedRecord?.idFailureReason
  const [activeStep, setActiveStep] = useState<PoaStep>(() =>
    getInitialStep(requiresRsa, stillRequiresAgeAfterRefusal, storedRecord),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)

  const selectRsaOutcome = (outcome: RsaOutcome) => {
    setStoredRsaOutcome(order.orderId, outcome)
    setActiveStep(outcome === 'passed' ? 'age' : 'refusal-reason')
  }

  const canConfirmRefusal = refusalReason !== null
    && (refusalReason !== 'other' || otherRefusalReason.trim().length > 0)

  const confirmRefusal = () => {
    if (!canConfirmRefusal || !refusalReason)
      return
    const record: RsaRefusalRecord = {
      orderId: order.orderId,
      reason: refusalReason,
      otherReason: refusalReason === 'other' ? otherRefusalReason.trim() : undefined,
      liquorItemsMarkedForRemoval: true,
    }
    storeRsaRefusal(record)
    onRsaRefusalRecorded?.(record)
    setActiveStep(stillRequiresAgeAfterRefusal ? 'age' : 'completion')
  }

  const selectAgeOutcome = (outcome: 'looks-25-or-older' | 'id-required') => {
    setStoredAgeOutcome(order.orderId, outcome)
    setActiveStep(outcome === 'looks-25-or-older' ? 'completion' : 'id')
  }

  const removedCategories: RestrictedCategory[] = [
    ...(rsaOutcome === 'refused' ? ['liquor' as const] : []),
    ...(idFailureReason ? categories.filter(category => category !== 'liquor' || rsaOutcome !== 'refused') : []),
  ].filter((category, index, all) => all.indexOf(category) === index)

  const hasRemoval = removedCategories.length > 0
  const script = hasRemoval
    ? refusalScriptCopy(removedCategories, idFailureReason ? 'id' : 'rsa')
    : undefined

  const recordedReasonLabel = recordedRefusal
    ? recordedRefusal.reason === 'other'
      ? recordedRefusal.otherReason
      : REFUSAL_REASON_LABELS[recordedRefusal.reason]
    : undefined
  // Both can be recorded together — an ID sighted and the reason it still failed —
  // so the summary shows whichever answers exist rather than only the first.
  const ageSummaryDetail = [
    idType ? ID_TYPE_LABELS[idType] : undefined,
    idFailureReason ? ID_FAILURE_LABELS[idFailureReason] : undefined,
  ].filter(Boolean).join(' · ') || undefined
  // A recorded failure keeps the age card red even when an ID was sighted: the two
  // answers are independent, and the outcome that matters is the failure.
  const ageTone = ageOutcome === 'looks-25-or-older'
    ? 'positive'
    : idFailureReason || !idType ? 'negative' : 'positive'

  const handleConfirmHandover = async () => {
    const submission: PoaSubmission = {
      orderId: order.orderId,
      restrictedCategories: categories,
      rsaOutcome: storedRecord?.rsaOutcome,
      rsaRecordedAt: storedRecord?.rsaRecordedAt,
      rsaRefusal: storedRecord?.rsaRefusal
        ? {
            reason: storedRecord.rsaRefusal.reason,
            otherReason: storedRecord.rsaRefusal.otherReason,
            liquorItemsMarkedForRemoval: true,
          }
        : undefined,
      ageOutcome: storedRecord?.ageOutcome,
      ageRecordedAt: storedRecord?.ageRecordedAt,
      idType: storedRecord?.idType,
      idFailureReason: storedRecord?.idFailureReason,
      idRecordedAt: storedRecord?.idRecordedAt,
      itemsMarkedForRemoval: removedCategories,
      completedAt: new Date().toISOString(),
    }
    setIsSubmitting(true)
    setSubmissionError(null)
    try {
      const outcome = await onComplete?.(submission)
      // The backend has the record now, so the local working copy is discarded —
      // completed compliance data should not sit in localStorage. Only on success:
      // a failure keeps the answers so the handover can be retried. When the
      // collection code step still has to submit, that step clears it instead.
      if (outcome === undefined || outcome === 'completed') {
        clearOrderRecord(order.orderId)
      }
    } catch (error) {
      console.error('Failed to complete Proof of Age checks:', error)
      setSubmissionError('Unable to complete the order. Check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasIdAnswer = Boolean(idType) || Boolean(idFailureReason)
  const handoverLabel = hasRemoval
    ? `Remove ${removalCtaLabel(removedCategories)} and handover order`
    : 'Confirm and hand over'

  return (
    <section aria-label="Proof of Age checks" aria-live="polite" className={classes.panel} data-testid="poa-flow-panel">
      <div className={classes.cards}>
        {rsaOutcome && activeStep !== 'rsa' && (
          <CheckSummaryRow
            label="RSA check"
            value={rsaOutcome === 'passed' ? 'Fit to receive liquor' : 'Not fit - Refusing liquor'}
            detail={activeStep === 'refusal-reason' ? undefined : recordedReasonLabel}
            tone={rsaOutcome === 'passed' ? 'positive' : 'negative'}
            testId="rsa-check-summary"
            changeLabel="Change RSA check"
            onChange={() => setActiveStep('rsa')}
          />
        )}
        {ageOutcome && activeStep !== 'age' && (
          <CheckSummaryRow
            label="Age"
            value={ageOutcome === 'looks-25-or-older' ? 'Looks 25 or older' : 'Doesn’t look 25. ID required'}
            detail={activeStep === 'id' ? undefined : ageSummaryDetail}
            tone={ageTone}
            testId="poa-age-check-summary"
            changeLabel="Change age check"
            onChange={() => setActiveStep('age')}
          />
        )}

        {activeStep === 'rsa' && (
          <RsaAssessment
            selectedOutcome={rsaOutcome}
            onPass={() => selectRsaOutcome('passed')}
            onRefuse={() => selectRsaOutcome('refused')}
          />
        )}
        {activeStep === 'age' && (
          <AgeCheckStep
            stepNumber={requiresRsa ? 2 : 1}
            totalSteps={totalSteps}
            selectedOutcome={ageOutcome}
            onLooks25OrOlder={() => selectAgeOutcome('looks-25-or-older')}
            onCheckId={() => selectAgeOutcome('id-required')}
          />
        )}
        {activeStep === 'id' && (
          <IdVerificationStep
            idType={idType}
            failureReason={idFailureReason}
            minimumDob={formatMinimumDob()}
            script={script}
            onIdType={selectedIdType => setStoredIdType(order.orderId, selectedIdType)}
            onFailure={reason => setStoredIdFailure(order.orderId, reason)}
          />
        )}
        {activeStep === 'refusal-reason' && (
          <RefusalReasonStep
            reason={refusalReason}
            otherReason={otherRefusalReason}
            onReasonChange={reason => setStoredRefusalDraft(order.orderId, { reason })}
            onOtherReasonChange={otherReason => setStoredRefusalDraft(order.orderId, { otherReason })}
          />
        )}
        {activeStep === 'completion' && (
          <CompletionStep
            removalLabel={hasRemoval ? (soleCategory(removedCategories) ?? 'restricted items') : undefined}
            script={script}
          />
        )}

        {submissionError && <p role="alert" className={classes.submissionError}>{submissionError}</p>}
      </div>

      {(activeStep === 'refusal-reason' || activeStep === 'id' || activeStep === 'completion') && (
        <div className={classes.footer}>
          {activeStep === 'refusal-reason' && (
            <>
              <button type="button" className={classes.backAction} onClick={() => setActiveStep('rsa')}>Back</button>
              <button
                type="button"
                className={classes.handoverAction}
                disabled={!canConfirmRefusal}
                onClick={confirmRefusal}
              >
                Confirm refusal
              </button>
            </>
          )}
          {activeStep === 'id' && (
            <button
              type="button"
              className={classes.handoverAction}
              data-tone={hasRemoval ? 'negative' : undefined}
              data-testid="poa-id-continue"
              disabled={!hasIdAnswer || isSubmitting}
              onClick={handleConfirmHandover}
            >
              {hasIdAnswer ? handoverLabel : 'Pick an ID to continue'}
            </button>
          )}
          {activeStep === 'completion' && (
            <button
              type="button"
              className={classes.handoverAction}
              data-tone={hasRemoval ? 'negative' : undefined}
              disabled={isSubmitting}
              onClick={handleConfirmHandover}
            >
              {handoverLabel}
            </button>
          )}
        </div>
      )}
    </section>
  )
}
